"use client";

import { Experience } from "@ninetailed/experience.js-react";
import { useInspectorMode } from "@utils/ContentfulUtils";
import { db } from "@utils/IndexedDb";
import contentfulDeafultLoader from "@utils/imageLoader";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import CustomImage from "@/components/ui/Image/Image";
import { searchInteractionTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useMediaQuery from "@/hooks/useMediaQuery";
import { parseCustomExperience } from "@/lib/experiences";
import { BaseEntry, Media, PRODUCT_TYPE, VISUAL_VARIANT } from "@/types";

const FilterBar = dynamic(() => import("@/components/ui/Form/Filters/FilterBar"), { ssr: false });

export interface HeroFirstCard extends BaseEntry {
  video?: Media;
  video_mobile?: Media;
  backupVideoImage: Media;
  image?: Media;
  mobileImage?: Media;
  logo?: Media;
  logoScrollDownImage?: Media;
  displaySearchBar?: boolean;
  stickySearchBar?: boolean;
}

interface Props {
  entry: HeroFirstCard;
}

interface InnerProps extends Partial<HeroFirstCard> {
  fields?: HeroFirstCard;
  baselineSticky?: boolean;
}

// Defined at module level so its identity is stable across parent renders.
// If it were declared inside HeroFirstCard, every parent render would create a new component type
// and React would unmount/remount the whole hero (including the <video>, re-downloading it).
const HeroFirstCardInner = (props: InnerProps) => {
  const finalEntry: any = props?.fields || props;
  const baselineSticky = props.baselineSticky;

  const inspectorMode = useInspectorMode(finalEntry.id);
  const { isMd } = useBreakPoint("md");
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const loadedVideoSrc = useRef<string>();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const poster = {
    src: contentfulDeafultLoader({ src: finalEntry?.backupVideoImage?.url, width: 1280, quality: `${Number(process.env.IMG_QUALITY ?? 60)}` }) ?? "",
  };

  const propertyTypeRecords = useLiveQuery(async () => {
    const projectCount = await db?.PROPERTY_TYPE?.count();
    return projectCount || 0;
  });

  const desktopVideoUrl = finalEntry?.video?.url;
  const mobileVideoUrl = finalEntry?.video_mobile?.url;
  const heroImageSrc = isMd ? finalEntry?.image?.url || "" : finalEntry?.mobileImage?.url || "";
  const backupImageSrc = finalEntry?.backupVideoImage?.url;
  const heroImageFallback = backupImageSrc && backupImageSrc !== heroImageSrc ? backupImageSrc : "/assets/apartment.png";

  useEffect(() => {
    // Wait until the media query has resolved, otherwise the mobile video is loaded first and then the desktop one
    if (!inView || !videoRef.current || !desktopVideoUrl || isDesktop === undefined) return;

    const src = isDesktop ? desktopVideoUrl : mobileVideoUrl || desktopVideoUrl;
    if (loadedVideoSrc.current === src) return;
    loadedVideoSrc.current = src;

    videoRef.current.src = src;
    videoRef.current.load();
    videoRef.current
      .play()
      .then(() => setIsVideoLoaded(true))
      .catch((error) => console.warn("Autoplay failed:", error));
  }, [inView, isDesktop, desktopVideoUrl, mobileVideoUrl]);

  return (
    <div className="relative flex justify-center h-svh" {...inspectorMode.getProps("video")} ref={ref}>
      <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
      {finalEntry?.video ? (
        <>
          <CustomImage
            src={poster.src}
            width={1024}
            height={768}
            alt=""
            fallbackSrc="/assets/apartment.png"
            className={`h-full w-full object-cover ${isVideoLoaded ? "hidden" : "block"} shimmer`}
          />
          <video className={`videoHero ${isVideoLoaded ? "block" : "hidden"}`} poster={poster.src} muted playsInline loop ref={videoRef}></video>
        </>
      ) : (
        <CustomImage
          src={heroImageSrc}
          width={isMd ? finalEntry?.image?.width : finalEntry?.mobileImage?.width}
          height={isMd ? finalEntry?.image?.height : finalEntry?.mobileImage?.height}
          fallbackSrc={heroImageFallback}
          className="h-full w-full"
          alt={finalEntry?.image?.title || ""}
        />
      )}
      {finalEntry?.logoScrollDownImage && (
        <div className="absolute bottom-3xl left-1/2 -translate-x-1/2 -translate-y-1/2 mb-xl">
          <div className="flex flex-col items-center mt-[20px] ijaz">
            <CustomImage src={finalEntry?.logoScrollDownImage?.url} alt={finalEntry?.logoScrollDownImage?.title} width={177} height={90} />
          </div>
        </div>
      )}
      {finalEntry?.logo ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mb-xl">
          <div className="flex-col items-center hidden md:flex">
            <Image
              src={finalEntry?.logo?.url}
              alt={finalEntry?.logo?.title}
              width={310}
              height={157}
              priority
              className="w-[177px] h-auto md:w-[310px]"
            />
          </div>
          <div className="flex flex-col items-center md:hidden">
            <Image src={finalEntry?.logo?.url} alt={finalEntry?.logo?.title} width={177} height={90} />
          </div>
        </div>
      ) : finalEntry?.displaySearchBar || finalEntry?.stickySearchBar || baselineSticky ? (
        <AnimatePresence>
          {!!propertyTypeRecords && propertyTypeRecords > 0 && (
            <motion.div
              className={`${finalEntry?.stickySearchBar || baselineSticky ? "fixed bottom-xl z-10 left-1/2 w-22/24" : "absolute left-1/2 bottom-xl z-10 w-22/24"}`}
              initial={{ opacity: 0, y: 50, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              transition={{ duration: 1 }}
              onMouseDown={() => {
                if (isMd) {
                  searchInteractionTracking();
                }
              }}
            >
              <div>
                <FilterBar name="hero-first-card" filters={[PRODUCT_TYPE.PROPERTY_TYPE, "bedrooms", "price"]} variant={VISUAL_VARIANT.DARK} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <></>
      )}
    </div>
  );
};

export default function HeroFirstCard({ entry }: Props) {
  // Memoised so Experience does not get a new experiences array (and re-resolve variants) on every render
  const parsedExperiences = useMemo(() => parseCustomExperience(entry), [entry]);
  const passthroughProps = useMemo(() => ({ baselineSticky: entry?.stickySearchBar }), [entry?.stickySearchBar]);

  return (
    <Experience
      {...entry}
      key={`experience-${entry.id}`}
      id={entry.id as any}
      component={HeroFirstCardInner as any}
      experiences={parsedExperiences}
      passthroughProps={passthroughProps as any}
    />
  );
}
